import { useState, useRef, useEffect } from 'react';
import { GuideStep, GuideStepType, StepTranslation } from '@/services/apis/guides';
import guidesApi from '@/services/apis/guides';
import { Button, Spinner, Progress, addToast } from '@heroui/react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GuideStepAudioPlayer = ({
  step,
  stepTranslation,
}: {
  step: GuideStep;
  stepTranslation: StepTranslation | null;
}) => {
  const { t } = useTranslation('guides');
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const volumePercentage = Math.round(volume * 100);

  // Only render for RICH_TEXT step types
  if (step.type !== GuideStepType.RICH_TEXT) {
    return null;
  }

  if (!stepTranslation) {
    return null;
  }

  // Stop audio when step changes
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      setIsPlaying(false);
      setCurrentTime(0);
    };
  }, [step.id]);

  // Update volume when volume state changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Start progress tracking when playing
  useEffect(() => {
    if (isPlaying && audioRef.current) {
      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
          setDuration(audioRef.current.duration || 0);
        }
      }, 100);
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    // cleanup function to reset the audio player
    return () => {
      setCurrentTime(0);
      setIsPlaying(false);
      setAudioUrl(null);
      setIsLoading(false);
    };
  }, []);

  const handlePlay = async () => {
    if (audioRef.current && audioUrl) {
      // If audio is already loaded, just play/pause
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (err) {
          console.error('Error playing audio:', err);
          addToast({
            title: t('audioPlayer.playError', { defaultValue: 'Failed to play audio' }),
            color: 'danger',
          });
        }
      }
      return;
    }

    // Fetch or generate audio
    setIsLoading(true);

    try {
      const response = await guidesApi.getStepAudio(step.id, stepTranslation.language);

      if (response.success && response.data) {
        setAudioUrl(response.data.audioUrl);
        // Audio will start playing once loaded
      } else {
        throw new Error(response.message || 'Failed to get audio');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load audio';
      addToast({
        title: t('audioPlayer.loadError', { defaultValue: 'Failed to load audio' }),
        description: errorMessage,
        color: 'danger',
      });
      console.error('Error loading audio:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAudioLoaded = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 0);
      audioRef.current.volume = isMuted ? 0 : volume;
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((err) => {
          console.error('Error playing audio:', err);
          addToast({
            title: t('audioPlayer.playError', { defaultValue: 'Failed to play audio' }),
            color: 'danger',
          });
        });
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleAudioError = () => {
    setIsPlaying(false);
    addToast({
      title: t('audioPlayer.loadError', { defaultValue: 'Failed to load audio' }),
      description: t('audioPlayer.loadErrorDescription', { defaultValue: 'There was an issue loading the audio file' }),
      color: 'danger',
    });
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  return (
    <div className="mt-8 pt-6 border-t border-divider">
      <div className="bg-content1 rounded-lg p-4 shadow-sm">
        {/* Main Controls */}
        <div className="flex items-center gap-4 mb-4">
          {/* Play/Pause Button */}
          <Button
            color="primary"
            size="lg"
            isIconOnly
            onPress={isPlaying ? handlePause : handlePlay}
            isDisabled={isLoading}
            className="min-w-[56px] h-14"
            radius="full"
          >
            {isLoading ? (
              <Spinner size="lg" color="current" />
            ) : isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6" />
            )}
          </Button>

          {/* Progress Section */}
          <div className="flex-1 mt-4">
            {/* Progress Bar */}
            <Progress
              aria-label={isLoading ? 'Loading...' : 'Playing...'}
              isIndeterminate={isLoading}
              value={progress}
              className="w-full mb-2 slider"
              size="md"
            />
            {/* Time Display */}
            <div className="flex items-center justify-between text-sm text-default-500">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Volume Control */}
          <div className="flex items-center gap-2 min-w-[120px] mt-[-10px]">
            <Button
              isIconOnly
              variant="flat"
              size="sm"
              onPress={handleMuteToggle}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </Button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              // className="flex-1 h-2 bg-default-200 rounded-lg appearance-none cursor-pointer slider"
              aria-label="Volume control"
              title={`Volume: ${volumePercentage}%`}
              className="w-24 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              style={{
                background: `linear-gradient(to right,rgb(85, 131, 247) 0%, rgb(85, 131, 247) ${volumePercentage}%, #334155 ${volumePercentage}%, #334155 100%)`,
              }}
            />
            <span className="text-foreground text-sm w-10">{volumePercentage}%</span>
          </div>
        </div>

        {/* Hidden Audio Element */}
        {audioUrl && (
          <audio
            ref={audioRef}
            src={audioUrl}
            onLoadedData={handleAudioLoaded}
            onTimeUpdate={() => {
              if (audioRef.current) {
                setCurrentTime(audioRef.current.currentTime);
              }
            }}
            onDurationChange={() => {
              if (audioRef.current) {
                setDuration(audioRef.current.duration || 0);
              }
            }}
            onEnded={handleAudioEnded}
            onError={handleAudioError}
            className="hidden"
          />
        )}
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: hsl(var(--nextui-primary));
          cursor: pointer;
        }

        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: hsl(var(--nextui-primary));
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default GuideStepAudioPlayer;
